/**
 * Editor do **Resumo Diário** — layout aluno × eixo.
 *
 * Modelo: Data × Turno × Grupo × Valor (Ruim / Regular / Bom).
 * Rascunho local (`wb_ops_daily_summary_draft:*`); envio → API / TOTVS.
 */

import PropTypes from "prop-types";
import React, { Component } from "react";
import { withAlert } from "react-alert";
import {
  FormControl,
  Grid,
  InputLabel,
  Button,
  Select,
  CircularProgress,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
  Tooltip,
  TextField
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CommentOutlinedIcon from "@material-ui/icons/CommentOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import styled from "styled-components";
import BackButton from "../../components/BackButton";
import RoundedAvatar from "../../waybee-ui/RoundedAvatar";
import Group from "../../waybee-ui/Group";
import DailySummaryService from "../../services/DailySummaryService";
import {
  DS_STATUS,
  computeStatus,
  cycleConceptId,
  loadDraft,
  orderedConceptIds,
  saveDraft,
  statusLabel
} from "./wbOpsDailySummaryDraft";
import { AbilityContext, Can } from "../../acl/can";

/**
 * Material Icons “clássico” do app não tem glyphs novos (soap, cleaning_services…).
 * Mapeia para ícones presentes no font set embutido.
 */
const AXIS_ICON_FALLBACK = {
  soap: "spa",
  cleaning_services: "spa",
  wash: "spa",
  sanitizer: "spa"
};

const Container = styled.div`
  width: 100%;
`;

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const StatusHint = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const TableScrollArea = styled.div`
  position: relative;
  width: 100%;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e4e3e2;
  border-radius: 4px;
  scroll-behavior: smooth;
`;

/** Seta overlay (estilo do print) — só quando há mais eixos à direita/esquerda. */
const OverlayScrollArrow = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 6;
  width: 44px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.95);
  background: ${({ $side }) =>
    $side === "right"
      ? "linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.38))"
      : "linear-gradient(to left, rgba(0,0,0,0), rgba(0,0,0,0.38))"};
  ${({ $side }) => ($side === "right" ? "right: 0;" : "left: 0;")}

  svg {
    font-size: 40px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
  }

  &:hover {
    background: ${({ $side }) =>
      $side === "right"
        ? "linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.5))"
        : "linear-gradient(to left, rgba(0,0,0,0), rgba(0,0,0,0.5))"};
  }
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;

  th,
  td {
    border-bottom: 1px solid #eee;
    vertical-align: middle;
  }
`;

const StudentTh = styled.th`
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fff;
  min-width: 260px;
  width: 260px;
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  border-right: 1px solid #eee;
`;

const AxisTh = styled.th`
  min-width: 168px;
  max-width: 200px;
  padding: 12px 8px 10px;
  text-align: center;
  background: ${({ $bg }) => $bg || "#fafafa"};
`;

const AxisTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${({ $color }) => $color || "#424242"};
  font-weight: 600;
  font-size: 13px;
  line-height: 1.2;

  .material-icons {
    font-size: 36px;
    line-height: 1;
  }
`;

const HeaderDefault = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
`;

const StudentTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  padding: 10px 12px;
  border-right: 1px solid #eee;
  min-width: 260px;
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StudentMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const StudentNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  .profile-link {
    opacity: 0;
    transition: opacity 0.15s ease;
    padding: 2px;
    color: ${({ theme }) => theme.color.primary.main};
  }

  &:hover .profile-link {
    opacity: 1;
  }
`;

const StudentName = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const StudentRa = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 3px;
`;

const AbsentHint = styled.div`
  font-size: 11px;
  color: #c62828;
  margin-top: 4px;
`;

const CellTd = styled.td`
  text-align: center;
  padding: 10px 6px;
  background: ${({ $bg }) => $bg || "transparent"};
  opacity: ${({ $disabled }) => ($disabled ? 0.35 : 1)};
`;

const ConceptControl = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
`;

const ConceptFace = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  gap: 2px;
`;

const ConceptIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg || "#f5f5f5"};
  color: ${({ $color }) => $color || "#757575"};

  .material-icons {
    font-size: 24px;
  }
`;

const ConceptLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.1;
  max-width: 72px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyDash = styled.span`
  color: rgba(0, 0, 0, 0.3);
  font-size: 20px;
  font-weight: 500;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const CommentToggle = styled(IconButton)`
  && {
    padding: 2px;
    color: ${({ $active, theme }) =>
      $active ? theme.color.primary.main : "rgba(0, 0, 0, 0.35)"};
  }
`;

const CommentField = styled(TextField)`
  && {
    width: 100%;
    max-width: 150px;
    margin-top: 2px;

    .MuiInputBase-input {
      font-size: 12px;
      line-height: 1.3;
    }
  }
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const chipColor = status => {
  if (status === DS_STATUS.SENT) return "default";
  if (status === DS_STATUS.READY) return "primary";
  return "secondary";
};

class DailySummary extends Component {
  constructor(props) {
    super(props);
    const { location } = props;
    const params = new URLSearchParams((location && location.search) || "");
    this.prefillDate = params.get("date") || null;
    this.prefillMomentId = params.get("moment") || null;
    this.saveTimer = null;
    this.tableScrollRef = React.createRef();
    this.state = {
      rawData: null,
      filter: {},
      dates: [],
      moments: [],
      concepts: [],
      values: {},
      comments: {},
      commentOpen: {},
      absent: {},
      absenceIds: {},
      headerDefaults: {},
      status: DS_STATUS.DRAFT,
      forcedSent: false,
      loadingFilters: true,
      loadingReport: true,
      savingHint: "",
      canScrollLeft: false,
      canScrollRight: false
    };
  }

  componentDidMount() {
    this.getConcepts();
    window.addEventListener("resize", this.updateScrollHints);
  }

  componentDidUpdate(prevProps, prevState) {
    const { loadingReport, rawData } = this.state;
    if (
      prevState.loadingReport !== loadingReport ||
      prevState.rawData !== rawData
    ) {
      this.updateScrollHints();
    }
  }

  componentWillUnmount() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    window.removeEventListener("resize", this.updateScrollHints);
  }

  setFilter(field, value, callback) {
    const { filter } = this.state;
    this.setState(
      {
        filter: {
          ...filter,
          [field]: value
        }
      },
      callback
    );
  }

  updateScrollHints = () => {
    const el = this.tableScrollRef.current;
    if (!el) {
      this.setState({ canScrollLeft: false, canScrollRight: false });
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    const canScrollRight = maxScroll > 4 && el.scrollLeft < maxScroll - 4;
    const canScrollLeft = el.scrollLeft > 4;
    const { canScrollLeft: prevL, canScrollRight: prevR } = this.state;
    if (prevL !== canScrollLeft || prevR !== canScrollRight) {
      this.setState({ canScrollLeft, canScrollRight });
    }
  };

  scrollAxes = direction => {
    const el = this.tableScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 220, behavior: "smooth" });
    setTimeout(this.updateScrollHints, 280);
  };

  /** Volta para a lista principal do Resumo Diário da turma. */
  listPath = () => {
    const { match } = this.props;
    if (match && match.params && match.params.id) {
      return `/classes/${encodeURIComponent(match.params.id)}/dailySummary`;
    }
    return "/classes";
  };

  getMoments = async () => {
    const { alert, t } = this.props;
    try {
      const moments = await DailySummaryService.getMoments();
      const fromQuery = this.prefillMomentId
        ? moments.find(m => String(m.id) === String(this.prefillMomentId))
        : null;
      this.prefillMomentId = null;
      const current =
        fromQuery || moments.find(m => m.current) || moments[0] || null;
      if (!current) {
        this.setState({ moments, loadingFilters: false, loadingReport: false });
        return;
      }
      this.setState(
        prevState => ({
          moments,
          filter: { ...prevState.filter, moment: current.id }
        }),
        () => {
          this.getData();
          this.setState({ loadingFilters: false });
        }
      );
    } catch (e) {
      console.error(e);
      alert.show(t("dailySummary.error.moments"), {
        title: t("common.actions.error")
      });
      this.setState({ loadingFilters: false });
    }
  };

  getDates = async () => {
    const { alert, t } = this.props;
    this.setState({ loadingFilters: true });
    try {
      const dates = await DailySummaryService.getDateOptions();
      if (dates && dates.length) {
        const fromQuery = this.prefillDate
          ? dates.find(d => d.id === this.prefillDate)
          : null;
        this.prefillDate = null;
        const selected = fromQuery || dates.find(d => d.current) || dates[0];
        this.setState(
          prevState => ({
            dates,
            filter: { ...prevState.filter, date: selected.id }
          }),
          () => this.getMoments()
        );
      } else {
        this.setState({ dates: [], loadingFilters: false });
      }
    } catch (e) {
      console.error(e);
      alert.show(t("dailySummary.error.dates"), {
        title: t("common.actions.error")
      });
      this.setState({ loadingFilters: false });
    }
  };

  getConcepts = async () => {
    const { alert, t } = this.props;
    try {
      const concepts = await DailySummaryService.getConcepts();
      const filtered = (concepts || []).filter(
        c =>
          c && c.id != null && String(c.label).toLowerCase() !== "sem conceito"
      );
      this.setState({ concepts: filtered }, this.getDates);
    } catch (e) {
      console.error(e);
      alert.show(t("dailySummary.error.concepts"), {
        title: t("common.actions.error")
      });
    }
  };

  axisEntries = rawData => {
    const entries = [];
    (rawData && rawData.axis ? rawData.axis : []).forEach(ax => {
      const index = (ax.indexes || [])[0];
      if (!index) return;
      const rawIcon = ax.icon || "category";
      entries.push({
        axisId: index.id,
        name: ax.name,
        icon: AXIS_ICON_FALLBACK[rawIcon] || rawIcon,
        color: ax.color || "#90A4AE",
        codOcorrenciaGrupo: index.codOcorrenciaGrupo,
        index
      });
    });
    return entries;
  };

  hydrateFromServerAndDraft = (rawData, classId, date, momentId) => {
    const axes = this.axisEntries(rawData);
    const values = {};
    const comments = {};
    const commentOpen = {};
    const absent = {};
    const absenceIds = {};

    (rawData.students || []).forEach(student => {
      const ra = String(student.ra);
      values[ra] = {};
      comments[ra] = {};
      axes.forEach(ax => {
        const found = (student.concepts || []).find(c => c.id === ax.axisId);
        values[ra][ax.axisId] =
          found && found.value != null ? found.value : null;
        const note =
          found && found.observacoes != null ? String(found.observacoes) : "";
        comments[ra][ax.axisId] = note;
        if (note) {
          commentOpen[`${ra}::${ax.axisId}`] = true;
        }
      });
      if (student.absent || student.isAbsent) {
        absent[ra] = true;
      }
      if (student.idOcorAlunoFalta) {
        absenceIds[ra] = student.idOcorAlunoFalta;
      }
    });

    const draft = loadDraft(classId, date, momentId);
    let headerDefaults = {};
    let forcedSent = false;

    if (draft) {
      if (draft.values) {
        Object.keys(draft.values).forEach(ra => {
          values[ra] = { ...(values[ra] || {}), ...draft.values[ra] };
        });
      }
      if (draft.comments) {
        Object.keys(draft.comments).forEach(ra => {
          comments[ra] = { ...(comments[ra] || {}), ...draft.comments[ra] };
          Object.keys(draft.comments[ra] || {}).forEach(axisId => {
            if (draft.comments[ra][axisId]) {
              commentOpen[`${ra}::${axisId}`] = true;
            }
          });
        });
      }
      if (draft.absent) {
        Object.assign(absent, draft.absent);
      }
      if (draft.absenceIds) {
        Object.assign(absenceIds, draft.absenceIds);
      }
      headerDefaults = draft.headerDefaults || {};
      forcedSent = draft.status === DS_STATUS.SENT;
    }

    const status = computeStatus({
      students: rawData.students,
      axisIds: axes.map(a => a.axisId),
      values,
      absent,
      forcedSent
    });

    return {
      values,
      comments,
      commentOpen,
      absent,
      absenceIds,
      headerDefaults,
      status,
      forcedSent
    };
  };

  scheduleAutoSave = () => {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(this.persistDraft, 350);
  };

  persistDraft = () => {
    const { match } = this.props;
    const {
      filter,
      values,
      comments,
      absent,
      absenceIds,
      headerDefaults,
      forcedSent,
      rawData
    } = this.state;
    const classId = match && match.params ? match.params.id : null;
    if (!classId || !filter.date || !filter.moment || !rawData) return;

    const nextStatus = computeStatus({
      students: rawData.students,
      axisIds: this.axisEntries(rawData).map(a => a.axisId),
      values,
      absent,
      forcedSent
    });

    saveDraft({
      classId,
      date: filter.date,
      momentId: filter.moment,
      values,
      comments,
      absent,
      absenceIds,
      headerDefaults,
      status: forcedSent ? DS_STATUS.SENT : nextStatus
    });

    this.setState({
      status: forcedSent ? DS_STATUS.SENT : nextStatus,
      savingHint: "draftSaved"
    });
    setTimeout(() => {
      this.setState(prev =>
        prev.savingHint === "draftSaved" ? { savingHint: "" } : null
      );
    }, 1500);
  };

  getData = async () => {
    const { filter } = this.state;
    const { alert, match, t } = this.props;
    const classId = match && match.params ? match.params.id : null;

    if (!filter.date || !filter.moment || !classId) {
      this.setState({
        loadingReport: false,
        rawData: null,
        values: {},
        absent: {}
      });
      return;
    }

    this.setState({ loadingReport: true });
    try {
      const rawData = await DailySummaryService.getReportData(
        classId,
        filter.date,
        filter.moment
      );
      const hydrated = this.hydrateFromServerAndDraft(
        rawData,
        classId,
        filter.date,
        filter.moment
      );
      this.setState({
        rawData,
        ...hydrated
      });
    } catch (e) {
      console.error(e);
      alert.show(t("dailySummary.error.load"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingReport: false });
    }
  };

  changeDate = value => {
    this.setState({ loadingReport: true });
    this.setFilter("date", value, this.getData);
  };

  changeMoment = value => {
    this.setState({ loadingReport: true });
    this.setFilter("moment", value, this.getData);
  };

  findConcept = conceptId => {
    const { concepts } = this.state;
    if (conceptId == null) return null;
    return concepts.find(c => String(c.id) === String(conceptId)) || null;
  };

  isReadOnly = () => {
    const { status, forcedSent } = this.state;
    return status === DS_STATUS.SENT || forcedSent;
  };

  onToggleAbsent = ra => {
    if (this.isReadOnly()) return;
    this.setState(prev => {
      const key = String(ra);
      const nextAbsent = { ...prev.absent, [key]: !prev.absent[key] };
      const axes = this.axisEntries(prev.rawData);
      const status = computeStatus({
        students: prev.rawData.students,
        axisIds: axes.map(a => a.axisId),
        values: prev.values,
        absent: nextAbsent,
        forcedSent: false
      });
      return { absent: nextAbsent, status, forcedSent: false };
    }, this.scheduleAutoSave);
  };

  onCycleCell = (ra, axisId, direction) => {
    if (this.isReadOnly()) return;
    const { absent, concepts } = this.state;
    if (absent[String(ra)]) return;
    const conceptIds = orderedConceptIds(concepts);
    this.setState(prev => {
      const key = String(ra);
      const current =
        prev.values[key] && prev.values[key][axisId] != null
          ? prev.values[key][axisId]
          : null;
      const nextId = cycleConceptId(current, conceptIds, direction);
      const values = {
        ...prev.values,
        [key]: {
          ...(prev.values[key] || {}),
          [axisId]: nextId
        }
      };
      const status = computeStatus({
        students: prev.rawData.students,
        axisIds: this.axisEntries(prev.rawData).map(a => a.axisId),
        values,
        absent: prev.absent,
        forcedSent: false
      });
      return { values, status, forcedSent: false };
    }, this.scheduleAutoSave);
  };

  onCycleHeader = (axisId, direction) => {
    if (this.isReadOnly()) return;
    const { concepts } = this.state;
    const conceptIds = orderedConceptIds(concepts);
    this.setState(prev => {
      const current =
        prev.headerDefaults[axisId] != null
          ? prev.headerDefaults[axisId]
          : null;
      const nextDefault = cycleConceptId(current, conceptIds, direction);
      const headerDefaults = {
        ...prev.headerDefaults,
        [axisId]: nextDefault
      };
      const values = { ...prev.values };
      (prev.rawData.students || []).forEach(student => {
        const ra = String(student.ra);
        if (prev.absent[ra]) return;
        values[ra] = {
          ...(values[ra] || {}),
          [axisId]: nextDefault
        };
      });
      const status = computeStatus({
        students: prev.rawData.students,
        axisIds: this.axisEntries(prev.rawData).map(a => a.axisId),
        values,
        absent: prev.absent,
        forcedSent: false
      });
      return { headerDefaults, values, status, forcedSent: false };
    }, this.scheduleAutoSave);
  };

  toggleCommentOpen = (ra, axisId) => {
    if (this.isReadOnly()) return;
    const key = `${ra}::${axisId}`;
    this.setState(prev => ({
      commentOpen: {
        ...prev.commentOpen,
        [key]: !prev.commentOpen[key]
      }
    }));
  };

  onCommentChange = (ra, axisId, text) => {
    if (this.isReadOnly()) return;
    this.setState(
      prev => ({
        comments: {
          ...prev.comments,
          [ra]: {
            ...(prev.comments[ra] || {}),
            [axisId]: text
          }
        }
      }),
      this.scheduleAutoSave
    );
  };

  send = async () => {
    const {
      rawData,
      filter,
      values,
      comments,
      absent,
      absenceIds,
      concepts,
      status,
      headerDefaults
    } = this.state;
    const { alert, match, t } = this.props;
    const classId = match && match.params ? match.params.id : null;

    if (status !== DS_STATUS.READY || this.isReadOnly()) return;

    this.setState({ loadingReport: true });
    try {
      const axes = this.axisEntries(rawData);
      const cells = [];
      const cleared = [];
      const absences = [];
      const clearedAbsences = [];

      (rawData.students || []).forEach(student => {
        const ra = String(student.ra);
        const wasAbsent = !!(student.absent || student.idOcorAlunoFalta);
        const isAbsent = !!absent[ra];

        if (isAbsent) {
          absences.push({
            ra: student.ra,
            idOcorAluno: absenceIds[ra] || student.idOcorAlunoFalta || undefined
          });
          axes.forEach(ax => {
            const previous = (student.concepts || []).find(
              c => c.id === ax.axisId
            );
            if (previous && previous.idOcorAluno) {
              cleared.push({
                ra: student.ra,
                codOcorrenciaGrupo: ax.codOcorrenciaGrupo,
                idOcorAluno: previous.idOcorAluno
              });
            }
          });
          return;
        }

        if (wasAbsent && (absenceIds[ra] || student.idOcorAlunoFalta)) {
          clearedAbsences.push({
            ra: student.ra,
            idOcorAluno: absenceIds[ra] || student.idOcorAlunoFalta
          });
        }

        axes.forEach(ax => {
          const conceptId = values[ra] && values[ra][ax.axisId];
          const previous = (student.concepts || []).find(
            c => c.id === ax.axisId
          );
          const idOcorAluno = previous && previous.idOcorAluno;
          const note =
            (comments[ra] && comments[ra][ax.axisId]) ||
            (previous && previous.observacoes) ||
            "";

          if (!conceptId) {
            if (idOcorAluno) {
              cleared.push({
                ra: student.ra,
                codOcorrenciaGrupo: ax.codOcorrenciaGrupo,
                idOcorAluno
              });
            }
            return;
          }

          const foundConcept = concepts.find(
            c => String(c.id) === String(conceptId)
          );
          if (!foundConcept) return;

          cells.push({
            ra: student.ra,
            codOcorrenciaGrupo: ax.codOcorrenciaGrupo,
            conceptLabel: foundConcept.tipoLabel || foundConcept.label,
            conceptId: foundConcept.id,
            idOcorAluno: idOcorAluno || undefined,
            observacoes: note
          });
        });
      });

      await DailySummaryService.save({
        classId,
        date: filter.date,
        momentId: filter.moment,
        idTurmaDisc: rawData && rawData.idTurmaDisc,
        cells,
        cleared,
        absences,
        clearedAbsences
      });

      saveDraft({
        classId,
        date: filter.date,
        momentId: filter.moment,
        values,
        comments,
        absent,
        absenceIds,
        headerDefaults,
        status: DS_STATUS.SENT
      });

      this.setState({
        forcedSent: true,
        status: DS_STATUS.SENT,
        savingHint: "sentHint"
      });
      alert.show(t("dailySummary.sent"), {
        title: t("common.actions.success")
      });
    } catch (e) {
      console.error(e);
      alert.show(t("dailySummary.error.send"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingReport: false });
    }
  };

  renderConceptControl = ({ conceptId, disabled, onPrev, onNext }) => {
    const { t } = this.props;
    const concept = this.findConcept(conceptId);
    return (
      <ConceptControl>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onPrev}
          aria-label={t("dailySummary.conceptPrev")}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <ConceptFace>
          {concept ? (
            <>
              <Tooltip title={concept.label}>
                <ConceptIcon $bg={concept.color} $color="rgba(0,0,0,0.65)">
                  <i className="material-icons">
                    {concept.icon || "sentiment_neutral"}
                  </i>
                </ConceptIcon>
              </Tooltip>
              <ConceptLabel>{concept.label}</ConceptLabel>
            </>
          ) : (
            <EmptyDash aria-label={t("dailySummary.noConcept")}>—</EmptyDash>
          )}
        </ConceptFace>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onNext}
          aria-label={t("dailySummary.conceptNext")}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </ConceptControl>
    );
  };

  render() {
    const {
      filter,
      dates,
      moments,
      rawData,
      values,
      comments,
      commentOpen,
      absent,
      headerDefaults,
      status,
      loadingFilters,
      loadingReport,
      savingHint,
      canScrollLeft,
      canScrollRight
    } = this.state;
    const { context } = this;
    const { t } = this.props;

    const axes = this.axisEntries(rawData);
    const readOnly = this.isReadOnly() || !context.can("save", "dailySummary");

    let statusHintText = t("dailySummary.autosave");
    if (savingHint) {
      statusHintText = t(`dailySummary.${savingHint}`);
    } else if (readOnly) {
      statusHintText = t("dailySummary.readOnly");
    }

    return (
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BackButton to={this.listPath()} />
          </Grid>
          <Grid item xs={12}>
            <Group fullWidth>
              {loadingFilters ? (
                <LoadContainer>
                  <CircularProgress />
                </LoadContainer>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("common.labels.date")}</InputLabel>
                      <Select
                        fullWidth
                        value={filter.date || ""}
                        onChange={e => this.changeDate(e.target.value)}
                        disabled={readOnly}
                      >
                        {dates.map(d => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("dailySummary.shift")}</InputLabel>
                      <Select
                        fullWidth
                        value={filter.moment || ""}
                        onChange={e => this.changeMoment(e.target.value)}
                        disabled={readOnly}
                      >
                        {moments.map(m => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Group>
          </Grid>

          <Grid item xs={12}>
            <Toolbar>
              <Chip
                label={statusLabel(status)}
                color={chipColor(status)}
                variant={status === DS_STATUS.DRAFT ? "outlined" : "default"}
              />
              <StatusHint>{statusHintText}</StatusHint>
            </Toolbar>

            {loadingReport || loadingFilters ? (
              <LoadContainer>
                <CircularProgress />
              </LoadContainer>
            ) : (
              <TableScrollArea>
                <TableWrap
                  ref={this.tableScrollRef}
                  onScroll={this.updateScrollHints}
                >
                  <Table>
                    <thead>
                      <tr>
                        <StudentTh>{t("dailySummary.student")}</StudentTh>
                        {axes.map(ax => {
                          const defId =
                            headerDefaults[ax.axisId] != null
                              ? headerDefaults[ax.axisId]
                              : null;
                          return (
                            <AxisTh key={ax.axisId} $bg={`${ax.color}22`}>
                              <AxisTitle $color={ax.color}>
                                <i className="material-icons">{ax.icon}</i>
                                <span>{ax.name}</span>
                              </AxisTitle>
                              <HeaderDefault>
                                {this.renderConceptControl({
                                  conceptId: defId,
                                  disabled: readOnly,
                                  onPrev: () =>
                                    this.onCycleHeader(ax.axisId, -1),
                                  onNext: () => this.onCycleHeader(ax.axisId, 1)
                                })}
                              </HeaderDefault>
                            </AxisTh>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {(rawData && rawData.students
                        ? rawData.students
                        : []
                      ).map(student => {
                        const ra = String(student.ra);
                        const isAbsent = !!absent[ra];
                        return (
                          <tr key={ra}>
                            <StudentTd>
                              <StudentRow>
                                <RoundedAvatar
                                  imageSrc={student.photo}
                                  size={48}
                                  imageAlt={student.name}
                                >
                                  {student.name}
                                </RoundedAvatar>
                                <StudentMeta>
                                  <StudentNameRow>
                                    <StudentName>{student.name}</StudentName>
                                    <Tooltip
                                      title={t("dailySummary.openProfile")}
                                    >
                                      <IconButton
                                        className="profile-link"
                                        size="small"
                                        aria-label={t(
                                          "dailySummary.profileAria"
                                        )}
                                        onClick={e => {
                                          e.stopPropagation();
                                          window.open(
                                            `/student/${encodeURIComponent(
                                              student.ra
                                            )}`,
                                            "_blank",
                                            "noopener,noreferrer"
                                          );
                                        }}
                                      >
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </StudentNameRow>
                                  <StudentRa>
                                    {t("common.labels.ra")}
                                    :
                                    {student.ra}
                                  </StudentRa>
                                  {student.absentEarlierSameDay && (
                                    <AbsentHint>
                                      {t("dailySummary.absentEarlier")}
                                    </AbsentHint>
                                  )}
                                  <FormControlLabel
                                    control={(
                                      <Checkbox
                                        color="primary"
                                        size="small"
                                        checked={isAbsent}
                                        disabled={readOnly}
                                        onChange={() => this.onToggleAbsent(ra)}
                                      />
                                    )}
                                    label={t("dailySummary.absence")}
                                  />
                                </StudentMeta>
                              </StudentRow>
                            </StudentTd>
                            {axes.map(ax => {
                              const conceptId =
                                values[ra] && values[ra][ax.axisId];
                              const concept = this.findConcept(conceptId);
                              const note =
                                (comments[ra] && comments[ra][ax.axisId]) || "";
                              const openKey = `${ra}::${ax.axisId}`;
                              const isCommentOpen = !!commentOpen[openKey];
                              return (
                                <CellTd
                                  key={openKey}
                                  $bg={
                                    concept ? concept.color : `${ax.color}14`
                                  }
                                  $disabled={isAbsent}
                                >
                                  <CellStack>
                                    {this.renderConceptControl({
                                      conceptId,
                                      disabled: readOnly || isAbsent,
                                      onPrev: () =>
                                        this.onCycleCell(ra, ax.axisId, -1),
                                      onNext: () =>
                                        this.onCycleCell(ra, ax.axisId, 1)
                                    })}
                                    {!isAbsent ? (
                                      <Tooltip
                                        title={
                                          note
                                            ? t("dailySummary.editComment")
                                            : t("dailySummary.addComment")
                                        }
                                      >
                                        <CommentToggle
                                          size="small"
                                          $active={!!note}
                                          disabled={readOnly}
                                          onClick={() =>
                                            this.toggleCommentOpen(
                                              ra,
                                              ax.axisId
                                            )}
                                          aria-label={t(
                                            "dailySummary.commentAria"
                                          )}
                                        >
                                          <CommentOutlinedIcon fontSize="small" />
                                        </CommentToggle>
                                      </Tooltip>
                                    ) : null}
                                    {isCommentOpen && !isAbsent ? (
                                      <CommentField
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        size="small"
                                        placeholder={t(
                                          "dailySummary.notePlaceholder"
                                        )}
                                        value={note}
                                        disabled={readOnly}
                                        onChange={e =>
                                          this.onCommentChange(
                                            ra,
                                            ax.axisId,
                                            e.target.value
                                          )}
                                      />
                                    ) : null}
                                  </CellStack>
                                </CellTd>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableWrap>
                {canScrollLeft ? (
                  <OverlayScrollArrow
                    type="button"
                    $side="left"
                    aria-label={t("dailySummary.prevAxes")}
                    onClick={() => this.scrollAxes(-1)}
                  >
                    <ChevronLeftIcon />
                  </OverlayScrollArrow>
                ) : null}
                {canScrollRight ? (
                  <OverlayScrollArrow
                    type="button"
                    $side="right"
                    aria-label={t("dailySummary.nextAxes")}
                    onClick={() => this.scrollAxes(1)}
                  >
                    <ChevronRightIcon />
                  </OverlayScrollArrow>
                ) : null}
              </TableScrollArea>
            )}

            <FooterBar>
              <Can I="send" a="dailySummary">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    status !== DS_STATUS.READY || readOnly || loadingReport
                  }
                  onClick={this.send}
                >
                  {t("dailySummary.send")}
                </Button>
              </Can>
            </FooterBar>
          </Grid>
        </Grid>
      </Container>
    );
  }
}

DailySummary.contextType = AbilityContext;

DailySummary.propTypes = {
  alert: PropTypes.shape({ show: PropTypes.func }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
    push: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string }),
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string })
  }).isRequired,
  t: PropTypes.func.isRequired
};

DailySummary.defaultProps = {
  location: { search: "" }
};

export default withAlert()(withRouter(withTranslation()(DailySummary)));
